<template>
  <div class="step-payment">
    <form
      class="form"
      @submit.prevent="handleSubmit"
    >
      <fieldset class="fieldset">
        <legend class="legend">
          Finish and Pay
        </legend>

        <h3 v-if="allowCash">
          Payment Method
        </h3>
        <h3 v-else>
          Credit or Debit Card
        </h3>

        <div
          v-if="allowCash"
          class="radio-group row"
        >
          <label
            class="label"
            for="credit"
          >
            <input
              id="credit"
              v-model="paymentMethod"
              type="radio"
              name="payment-method"
              value="credit"
            >
            Credit or Debit Card
          </label>
          <label
            class="label"
            for="cash"
          >
            <input
              id="cash"
              v-model="paymentMethod"
              type="radio"
              name="payment-method"
              value="cash"
            >
            Cash
          </label>
        </div>

        <div
          v-if="paymentMethod === 'cash'"
          class="cash-payment"
        >
          <h3>How cash payment works</h3>
          <p>
            Select the cash option to recieve a barcode that you can print or
            send to your phone. Go to the nearest 7-Eleven, CVS, or Family
            Dollar, show the cashier the barcode, and make a payment in cash.
          </p>
          <p>
            We’ll remind you to make a new payment at the end of your 30 days.
            To keep your pass active, pay your next month’s fee and any usage
            charges at participating locations.
          </p>
        </div>
        <div
          v-else
          class="row"
        >
          <div
            v-if="existingCard && registration.stripeToken"
            class="existing-card"
          >
            <span class="existing-card__token">
              <span class="existing-card__brand">{{
                registration.stripeToken.card.brand
              }}</span>
              <span
                class="existing-card__hidden-digits"
              >&bull;&bull;&bull;&bull;</span>
              <span class="existing-card__last4">{{
                registration.stripeToken.card.last4
              }}</span>
            </span>
            <button
              class="existing-card__remove-card button-link"
              @click.prevent="removeCard"
            >
              Change Card
            </button>
          </div>

          <StripeCard
            v-else
            :stripe-key="stripeKey"
            :options="cardOptions"
            @token="setToken"
          />
        </div>
      </fieldset>
      <fieldset
        v-if="!(acceptedEula && $store.state.supportsPayButton)"
        class="fieldset"
      >
        <div
          v-for="agreement in Object.keys(userAgreement)"
          :key="agreement"
          class="checkbox checkbox--inline"
        >
          <label
            class="checkbox__label"
            :for="`terms-${agreement}`"
          >
            <input
              :id="`terms-${agreement}`"
              v-model="userAgreement[agreement]"
              class="checkbox__input"
              type="checkbox"
              :name="`terms-${agreement}`"
              value="1"
            >
            <p v-html="$t('user-agreement')[agreement]" />
          </label>
        </div>
        <p
          v-if="!optOutEnabled"
          class="opt-out-enabler"
        >
          <a
            href="javascript:void(0)"
            @click="optOutEnabled = true"
          >Click here to update your email or text message preferences</a>
        </p>
        <div v-if="optOutEnabled">
          <div class="checkbox">
            <input
              id="email-opt-in"
              v-model="optInEmail"
              class="checkbox__input"
              type="checkbox"
              name="email-opt-in"
              value="1"
            >
            <label
              class="checkbox__label"
              for="email-opt-in"
            >Subscribe to the monthly newsletter</label>
          </div>

          <div
            v-if="allowOptInSms"
            class="checkbox"
          >
            <input
              id="phone-opt-in"
              v-model="optInSms"
              class="checkbox__input"
              type="checkbox"
              name="phone-opt-in"
              value="1"
            >
            <label
              class="checkbox__label"
              for="phone-opt-in"
            >Receive text message account notifications</label>
          </div>
        </div>
      </fieldset>
      <p
        v-if="!!passType && passType.isAutoRenewing"
        class="note"
      >
        Your pass will renew automatically<span v-if="passType.duration">
          every {{ passType.duration }} days</span>. Usage fees will be charged to your payment method daily. Cancel
        anytime.
      </p>
      <transition name="slide-down">
        <p
          v-if="paymentError"
          class="form__error"
        >
          {{ paymentError }}
        </p>
      </transition>
      <transition name="slide-down">
        <p
          v-if="registrationError"
          class="form__error"
        >
          {{ registrationError }}
        </p>
      </transition>
      <div class="navigation-buttons">
        <div
          class="input-container input-container--button"
          :class="$store.state.order.loading ? 'input-container--loading' : ''"
        >
          <input
            type="submit"
            :disabled="
              !acceptedEula || $store.state.order.loading || !hasStripeToken
            "
            class="button button--submit"
            :value="signupButtonText"
          >
        </div>
        <PreviousStepButton />
      </div>
    </form>
  </div>
</template>
<script>
import StripeCard from '../components/StripeCard.vue';
import PreviousStepButton from '../components/PreviousStepButton.vue';
import { getErrorMessage } from '../utils';

export default {
  components: {
    StripeCard,
    PreviousStepButton,
  },
  data() {
    return {
      existingCard: this.hasStripeToken,
      paymentMethod: this.$store.state.registration.paymentMethod,
      optInSms: this.$store.state.user.optInSms,
      optInEmail: this.$store.state.user.optInEmail,
      optOutEnabled:
        !this.$store.state.user.optInEmail || !this.$store.state.user.optInSms,
      allowOptInSms: this.$store.state.user.allowOptInSms,
      userAgreement: Object.assign(
        {},
        Object.fromEntries(
          new Map(
            Object.keys(this.$t('user-agreement')).map(agreement => [
              agreement,
              false,
            ]),
          ),
        ),
        this.$store.state.user.userAgreement,
      ),
    };
  },
  computed: {
    registration() {
      return this.$store.state.registration;
    },
    order() {
      return this.$store.state.order;
    },
    stripeKey() {
      return this.$store.state.stripeKey;
    },
    hasStripeToken() {
      const token = this.$store.state.registration.stripeToken;
      return token !== null && typeof token !== 'undefined';
    },
    cardOptions() {
      return {
        value: {
          // postalCode: this.$store.state.user.zipCode,
          hidePostalCode: false,
        },
      };
    },
    passType() {
      return this.$store.getters.passType;
    },
    allowCash() {
      if (this.passType === null) {
        return false;
      }
      return this.passType.allowCash || false;
    },
    signupButtonText() {
      return this.paymentMethod === 'cash' ? 'Sign Up' : 'Pay Now';
    },
    paymentError() {
      if (this.order.errorList && this.order.errorList.includes('PaymentError')) {
        if (this.$store.state.registration?.stripeToken?.card?.funding === 'prepaid') {
          return this.$t('error-prepaid-card');
        }
        return 'An error occurred processing your payment information. Please confirm your information and try again.';
      }
      return null;
    },
    registrationError() {
      const error = getErrorMessage(this.order.errorList);
      return error;
    },
    acceptedEula() {
      return Object.values(this.userAgreement).every(
        agreement => agreement === true,
      );
    },
  },
  methods: {
    confirmAndPay() {
      if (this.hasStripeToken) {
        this.$store.dispatch('updateRegistration', {
          paymentMethod: 'credit',
        });
        this.handleSubmit();
      }
    },
    setToken(token) {
      this.$store.dispatch('updateRegistration', {
        paymentMethod: 'credit',
        stripeToken: token,
      });
    },
    async continueWithCash() {
      this.$store.dispatch('updateRegistration', { paymentMethod: 'cash' });
      await this.$store.dispatch('submitRegistration');
      this.routeRegistration();
    },
    removeCard() {
      this.$store.dispatch('updateRegistration', { stripeToken: null });
      this.existingCard = false;
    },
    async handleSubmit() {
      if (!this.acceptedEula) {
        return;
      }
      this.$store.dispatch('updateUser', {
        optInEmail: this.optInEmail,
        optInSms: this.optInSms,
      });
      await this.$store.dispatch('submitRegistration');
      // TODO: route errors to the correct page
      this.routeRegistration();
    },
    routeRegistration() {
      const errorRoutes = {
        UserEmailInvalid: 'profile',
        UserEmailAlreadyInUse: 'profile',
        UserPasswordInvalid: 'profile',
        UserFirstNameInvalid: 'profile',
        UserLastNameInvalid: 'profile',
        UserPhoneNumberInvalid: this.$store.state.enableSms
          ? 'phoneVerification'
          : 'profile',
        UserZipcodeInvalid: 'address',
        UserAddressStreetInvalid: 'address',
        UserAddressCityInvalid: 'address',
        UserAddressStateInvalid: 'address',
      };
      if (this.order.errorList) {
        const error = Object.keys(errorRoutes).find(key => this.order.errorList.includes(key));
        if (error) {
          this.$router.push({ name: errorRoutes[error] });
        }
        return;
      }
      this.$router.push({ name: 'orderComplete' });
    },
  },
};
</script>
